<template>
  <div class="components-demo">
    <h1 class="page-title">组件示例</h1>

    <!-- 组件分类标签页 -->
    <div class="component-tabs">
      <div class="tab" :class="{ active: activeTab === 'button' }" @click="activeTab = 'button'">
        按钮组件
      </div>
      <div class="tab" :class="{ active: activeTab === 'input' }" @click="activeTab = 'input'">
        输入框组件
      </div>
      <div class="tab" :class="{ active: activeTab === 'select' }" @click="activeTab = 'select'">
        下拉框组件
      </div>
      <div class="tab" :class="{ active: activeTab === 'panel' }" @click="activeTab = 'panel'">
        折叠面板组件
      </div>
      <div class="tab" :class="{ active: activeTab === 'autocomplete' }" @click="activeTab = 'autocomplete'">
        自动完成组件
      </div>
      <div class="tab" :class="{ active: activeTab === 'message' }" @click="activeTab = 'message'">
        消息提示组件
      </div>
      <div class="tab" :class="{ active: activeTab === 'busi' }" @click="activeTab = 'busi'">
        业务组件
      </div>
    </div>

    <!-- 按钮组件区域 -->
    <div v-show="activeTab === 'button'" class="component-section">
      <div class="section-title">按钮组件</div>

      <div class="card-grid">
        <div class="component-card">
          <div class="card-header">默认按钮</div>
          <div class="card-content">
            <OnButton>默认按钮</OnButton>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">禁用状态</div>
          <div class="card-content">
            <OnButton disabled>禁用按钮</OnButton>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">加载状态</div>
          <div class="card-content">
            <OnButton :loading="isLoading" @click="toggleLoading">
              {{ isLoading ? '加载中' : '点击加载' }}
            </OnButton>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">自定义样式</div>
          <div class="card-content">
            <OnButton :customStyle="{ width: '100%' }" @click="handleClick">
              自定义按钮
            </OnButton>
          </div>
        </div>
      </div>
    </div>

    <!-- 输入框组件区域 -->
    <div v-show="activeTab === 'input'" class="component-section">
      <div class="section-title">输入框组件</div>

      <div class="card-grid">
        <div class="component-card">
          <div class="card-header">基础输入框</div>
          <div class="card-content">
            <OnInput v-model="inputValue" placeholder="请输入内容" />
            <div v-if="inputValue" class="result-text">当前输入: {{ inputValue }}</div>
            <div class="feature-hint">聚焦时placeholder会被清空（基础功能）</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">保留placeholder</div>
          <div class="card-content">
            <OnInput v-model="keepPlaceholderValue" placeholder="聚焦时不会清空此文本" :clearPlaceholderOnFocus="false" />
            <div class="feature-hint">通过设置clearPlaceholderOnFocus="false"可以保持placeholder</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">禁用状态</div>
          <div class="card-content">
            <OnInput disabled placeholder="禁用状态输入框" />
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">错误状态</div>
          <div class="card-content">
            <OnInput v-model="errorInputValue" placeholder="请输入内容(最少3个字符)" :error="!!errorMessage"
              @input="validateInput" />
            <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">必填验证</div>
          <div class="card-content">
            <div class="field-container">
              <span v-if="requiredValueError" class="required-error">{{ $t('message.validator.required') }}</span>
              <OnInput v-model="requiredValue" placeholder="必填字段 (失去焦点验证)" :error="requiredValueError"
                @blur="validateRequired" @input="resetRequiredError" />
            </div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">格式验证</div>
          <div class="card-content">
            <OnInput v-model="emailValue" placeholder="请输入电子邮箱" :error="!!emailError" @blur="validateEmail" />
            <div v-if="emailError" class="error-message">{{ emailError }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 下拉框组件区域 -->
    <div v-show="activeTab === 'select'" class="component-section">
      <div class="section-title">下拉框组件</div>

      <div class="card-grid">
        <div class="component-card">
          <div class="card-header">基础下拉框</div>
          <div class="card-content">
            <OnSelect v-model="selectedValue" :options="selectOptions" placeholder="请选择一个选项" />
            <div v-if="selectedValue" class="result-text">当前选中: {{ selectedValue }}</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">禁用状态</div>
          <div class="card-content">
            <OnSelect v-model="selectedValue2" :options="selectOptions" disabled placeholder="禁用状态" />
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">带禁用选项</div>
          <div class="card-content">
            <OnSelect v-model="selectedValue3" :options="selectOptionsWithDisabled" placeholder="包含禁用选项" />
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">错误状态</div>
          <div class="card-content">
            <OnSelect v-model="selectedError" :options="selectOptions" placeholder="请选择(不能选择选项1)"
              :error="!!selectErrorMessage" @change="validateSelect" />
            <div v-if="selectErrorMessage" class="error-message">{{ selectErrorMessage }}</div>
          </div>
        </div>

      </div>
    </div>

    <!-- 折叠面板组件区域 -->
    <div v-show="activeTab === 'panel'" class="component-section">
      <div class="section-title">折叠面板组件（手风琴效果）</div>

      <div class="panel-controls">
        <span class="control-label">控制全局箭头显示:</span>
        <el-switch v-model="showPanelArrow" active-text="显示箭头" inactive-text="隐藏箭头"></el-switch>
      </div>

      <div class="card-grid">
        <div class="component-card">
          <div class="card-header">支付方式选择示例</div>
          <div class="card-content">
            <OnCollapsiblePanel :modelValue.sync="selectedPaymentMethod" :accordion="true" key="payment-panel">
              <OnCollapsiblePanelItem v-for="item in paymentMethods" :key="item.name" :name="item.name"
                :title="item.title" :icon="item.icon" :disabled="item.disabled" :showArrow="showPanelArrow">
                <!-- 微信支付插槽 -->
                <template v-if="item.name === 'wechat'">
                  <div class="payment-info">
                    <p>请使用微信扫描下面的二维码完成支付</p>
                    <div class="qr-code-placeholder">
                      [二维码占位图]
                    </div>
                  </div>
                </template>

                <!-- 支付宝插槽 -->
                <template v-if="item.name === 'alipay'">
                  <div class="payment-info">
                    <p>请使用支付宝扫描下面的二维码完成支付</p>
                    <div class="qr-code-placeholder">
                      [二维码占位图]
                    </div>
                  </div>
                </template>

                <!-- 信用卡支付插槽 -->
                <template v-if="item.name === 'credit-card'">
                  <div class="payment-form">
                    <div class="form-group">
                      <label>卡号</label>
                      <OnInput placeholder="请输入信用卡号" />
                    </div>
                    <div class="form-row">
                      <div class="form-group">
                        <label>过期日期</label>
                        <OnInput placeholder="MM/YY" />
                      </div>
                      <div class="form-group">
                        <label>CVV</label>
                        <OnInput placeholder="123" />
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 额外内容 -->
                <template #extra>
                  <span v-if="item.name === 'wechat'" class="payment-badge">推荐</span>
                  <span v-if="item.name === 'disabled'" class="unavailable-tag">暂不可用</span>
                </template>
              </OnCollapsiblePanelItem>
            </OnCollapsiblePanel>

            <div class="continue-button" v-if="selectedPaymentMethod">
              <OnButton :customStyle="{ width: '100%', marginTop: '16px', background: '#1e40af', color: 'white' }">
                继续使用{{ getPaymentMethodName(selectedPaymentMethod) }}
              </OnButton>
            </div>
            <div class="feature-hint">
              当前支付方式: <b>{{ selectedPaymentMethod }}</b> |
              箭头显示状态: <b>{{ showPanelArrow ? '显示' : '隐藏' }}</b>
            </div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">多选模式示例</div>
          <div class="card-content">
            <div class="arrow-toggle-controls">
              <h4>箭头显示设置</h4>
              <el-radio-group v-model="categoryArrowType" size="small">
                <el-radio-button label="show">强制显示</el-radio-button>
                <el-radio-button label="hide">强制隐藏</el-radio-button>
              </el-radio-group>
            </div>

            <OnCollapsiblePanel :modelValue.sync="selectedCategories" :accordion="false" @change="handleCategoryChange">
              <OnCollapsiblePanelItem v-for="item in categoryItems" :key="item.name" :name="item.name"
                :title="item.title" :showArrow="getCategoryArrowVisibility()">
                <template v-if="item.name === 'category1'">
                  <div class="category-content">
                    <p>这是分类1的内容，可以同时打开多个分类</p>
                    <div class="tag-list">
                      <span class="tag">标签1</span>
                      <span class="tag">标签2</span>
                      <span class="tag">标签3</span>
                    </div>
                  </div>
                </template>

                <template v-if="item.name === 'category2'">
                  <div class="category-content">
                    <p>这是分类2的内容，多选模式下可以同时展开多个面板</p>
                    <div class="tag-list">
                      <span class="tag">标签A</span>
                      <span class="tag">标签B</span>
                    </div>
                  </div>
                </template>

                <template v-if="item.name === 'category3'">
                  <div class="category-content">
                    <p>这是分类3的内容</p>
                    <div class="tag-list">
                      <span class="tag">标签X</span>
                      <span class="tag">标签Y</span>
                      <span class="tag">标签Z</span>
                    </div>
                  </div>
                </template>
              </OnCollapsiblePanelItem>
            </OnCollapsiblePanel>

            <div class="selection-result mt-10">
              当前选中: {{ selectedCategories.join(', ') || '无' }} |
              箭头设置: <b>{{ categoryArrowTypeText }}</b>
            </div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">当前选中状态</div>
          <div class="card-content">
            <div class="selection-result">
              当前已选择: {{ getPaymentMethodName(selectedPaymentMethod) }}
            </div>
            <div class="action-buttons">
              <OnButton @click="selectedPaymentMethod = 'wechat'">选择微信支付</OnButton>
              <OnButton @click="selectedPaymentMethod = 'alipay'">选择支付宝</OnButton>
              <OnButton @click="selectedPaymentMethod = 'credit-card'">选择信用卡</OnButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 自动完成组件区域 -->
    <div v-show="activeTab === 'autocomplete'" class="component-section">
      <div class="section-title">自动完成组件</div>

      <div class="card-grid">
        <!-- 基础自动完成 -->
        <div class="component-card">
          <div class="card-header">基础用法</div>
          <div class="card-content">
            <OnAutocomplete v-model="autocompleteValue"
              :fetch-suggestions="(queryString, cb) => cb(autocompleteSuggestions)" placeholder="请输入内容" />
            <div v-if="autocompleteValue" class="result-text">当前输入: {{ autocompleteValue }}</div>
            <div class="feature-hint">基本的自动完成功能，支持字符串数组建议选项</div>
          </div>
        </div>

        <!-- 邮箱输入框示例 -->
        <div class="component-card">
          <div class="card-header">邮箱输入框</div>
          <div class="card-content">
            <OnAutocomplete v-model="autocompleteEmailValue" :fetch-suggestions="queryEmailSearch" placeholder="请输入邮箱地址"
              :trigger-on-focus="false" />
            <div v-if="autocompleteEmailValue" class="result-text">当前输入: {{ autocompleteEmailValue }}</div>
            <div class="feature-hint">输入@后显示常用邮箱域名建议，支持邮箱智能提示</div>
          </div>
        </div>

        <!-- 对象数组建议 -->
        <div class="component-card">
          <div class="card-header">对象数组建议</div>
          <div class="card-content">
            <OnAutocomplete v-model="countryValue" :fetch-suggestions="(queryString, cb) => cb(countrySuggestions)"
              placeholder="请输入国家" />
            <div v-if="countryValue" class="result-text">当前输入: {{ countryValue }}</div>
            <div class="feature-hint">支持{value, label}格式的对象数组，显示更丰富的内容</div>
          </div>
        </div>

        <!-- 禁用状态 -->
        <div class="component-card">
          <div class="card-header">禁用状态</div>
          <div class="card-content">
            <OnAutocomplete v-model="disabledAutocompleteValue"
              :fetch-suggestions="(queryString, cb) => cb(autocompleteSuggestions)" placeholder="禁用状态" disabled />
            <div class="feature-hint">禁用状态下不可输入，文本颜色为#9EA9C1</div>
          </div>
        </div>

        <!-- 错误状态 -->
        <div class="component-card">
          <div class="card-header">错误状态</div>
          <div class="card-content">
            <OnAutocomplete v-model="errorAutocompleteValue"
              :fetch-suggestions="(queryString, cb) => cb(autocompleteSuggestions)" placeholder="请输入内容"
              :error="!!autocompleteErrorMessage" @blur="validateAutocompleteInput" />
            <div class="feature-hint">失焦时验证内容，显示错误状态和错误信息</div>
          </div>
        </div>

        <h3 class="form-subtitle mt-20">Checkout用法示例</h3>
        <OnAutocomplete v-model="checkoutEmailValue" autocomplete="email" :trigger-on-focus="false"
          :fetch-suggestions="queryEmailSearch" :maxLength="256" />
        <div class="feature-hint">同checkout页面完全一致的用法，这里对接了同样的queryEmailSearch方法</div>
      </div>
    </div>

    <!-- 消息提示组件区域 -->
    <div v-show="activeTab === 'message'" class="component-section">
      <div class="section-title">消息提示组件</div>

      <div class="card-grid">
        <div class="component-card">
          <div class="card-header">成功提示</div>
          <div class="card-content">
            <OnButton @click="showSuccessMessage">显示成功消息</OnButton>
            <div class="feature-hint">点击按钮显示成功提示，3秒后自动关闭</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">错误提示</div>
          <div class="card-content">
            <OnButton @click="showErrorMessage">显示错误消息</OnButton>
            <div class="feature-hint">点击按钮显示错误提示，3秒后自动关闭</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">自定义时长</div>
          <div class="card-content">
            <OnButton @click="showLongSuccessMessage" class="mb-10">显示5秒成功消息</OnButton>
            <OnButton @click="showLongErrorMessage">显示5秒错误消息</OnButton>
            <div class="feature-hint">可以自定义消息显示时长</div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">多个消息</div>
          <div class="card-content">
            <OnButton @click="showMultipleMessages">连续显示多条消息</OnButton>
            <div class="feature-hint">支持同时显示多条消息</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 业务组件区域 -->
    <div v-show="activeTab === 'busi'" class="component-section">
      <div class="section-title">业务组件</div>

      <div class="card-grid">
        <div class="component-card full-width">
          <div class="card-header">信用卡信息表单</div>
          <div class="card-content card-form-content">
            <div class="card-form-wrapper">
              <h3 class="form-title">信用卡信息表单</h3>
              <CardInformationForm :cardNum.sync="cardInfo.cardNum" :effectiveDate.sync="cardInfo.effectiveDate"
                :cvv.sync="cardInfo.cvv" :rules="cardValidationRules" :cardTypeInfo="cardTypeList"
                @inputFocus="handleCardInputFocus" @cardNumberBlur="logBlurEvent('cardNumber', $event)"
                @effectiveDateBlur="logBlurEvent('effectiveDate', $event)" @cvvBlur="logBlurEvent('cvv', $event)" />

              <div class="mt-20 card-actions">
                <OnButton @click="validateCardInfo" style="background-color: #20296A; color: white;">验证卡信息</OnButton>
                <OnButton @click="resetCardInfo" class="ml-10">重置</OnButton>
              </div>
            </div>

            <div class="card-info-result mt-10" v-if="showCardResult">
              <div class="result-title">当前卡信息:</div>
              <div class="result-item">
                <span class="label">卡号:</span>
                <span class="value">{{ cardInfo.cardNum || '未输入' }}</span>
              </div>
              <div class="result-item">
                <span class="label">有效期:</span>
                <span class="value">{{ cardInfo.effectiveDate || '未输入' }}</span>
              </div>
              <div class="result-item">
                <span class="label">CVV:</span>
                <span class="value">{{ cardInfo.cvv || '未输入' }}</span>
              </div>
              <div class="result-item">
                <span class="label">验证结果:</span>
                <span class="value"
                  :class="{ 'text-success': cardValidationSuccess, 'text-error': !cardValidationSuccess }">
                  {{ cardValidationSuccess ? '验证通过' : '验证失败' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="component-card">
          <div class="card-header">国家选择组件</div>
          <div class="card-content">
            <h3 class="form-subtitle">标准国家选择</h3>
            <CountrySelect v-model="selectedCountry" @change="handleCountryChange" />
            <div class="result-text" v-if="selectedCountry">
              当前选中: {{ selectedCountry }} - {{ selectedCountryName }}
            </div>

            <h3 class="form-subtitle mt-20">仅显示常用国家</h3>
            <CountrySelect v-model="selectedCommonCountry" :onlyCommon="true" @change="handleCommonCountryChange" />

            <h3 class="form-subtitle mt-20">自定义常用国家</h3>
            <CountrySelect v-model="selectedCustomCountry" :onlyCommon="true" :commonCountries="customCountryList"
              @change="handleCustomCountryChange" />
          </div>
        </div>

        <!-- 倒计时组件示例 -->
        <div class="component-card">
          <div class="card-header">倒计时组件</div>
          <div class="card-content">
            <h3 class="form-subtitle">基础用法</h3>
            <OnCountdownTimer :merchantTxnTime="justBeforeThreeMin" :effectiveMinutes="30"
              @onClose="handleCountdownClose" @timeEnd="handleCountdownTimeEnd" />
            <div class="feature-hint">完整模式显示（已进入最后3分钟倒计时）</div>

            <h3 class="form-subtitle mt-20">简化模式</h3>
            <OnCountdownTimer :merchantTxnTime="justBeforeThreeMin" :effectiveMinutes="30" :simplified="true"
              @timeEnd="handleCountdownTimeEnd" />
            <div class="feature-hint">简化模式显示（已进入最后3分钟倒计时）</div>

          </div>
        </div>

        <!-- 错误面板组件示例 -->
        <div class="component-card">
          <div class="card-header">错误面板组件</div>
          <div class="card-content">
            <h3 class="form-subtitle">基础用法</h3>
            <OnErrorMsgPanel ref="errorPanel1" :visible="isErrorPanel1Visible" errorText="Here is an Error text"
              @onClose="handleErrorPanel1Close" />
            <div class="feature-hint">基础错误面板（点击右上角×可关闭）</div>
            <el-button size="small" class="mt-10" @click="showErrorPanel1">重新显示</el-button>

            <h3 class="form-subtitle mt-20">使用插槽自定义内容</h3>
            <OnErrorMsgPanel ref="errorPanel2" :visible="isErrorPanel2Visible" @onClose="handleErrorPanel2Close">
              <div>
                <p>自定义错误内容支持HTML格式化</p>
                <ul>
                  <li>支持列表项</li>
                  <li>支持<b>加粗</b>和<i>斜体</i></li>
                </ul>
              </div>
            </OnErrorMsgPanel>
            <div class="feature-hint">使用插槽可以自定义错误内容</div>
            <el-button size="small" class="mt-10" @click="showErrorPanel2">重新显示</el-button>
          </div>
        </div>

        <!-- 支付选项选择器组件示例 -->
        <div class="component-card full-width">
          <div class="card-header">支付选项选择器组件</div>
          <div class="card-content">
            <h3 class="form-subtitle">DOKU虚拟账户支付</h3>
            <OnPaymentOptionSelector cardType="DOKU_VA" :selectedCode="selectedPaymentOption"
              @select="handlePaymentOptionSelect" />
            <div class="selection-result mt-10">
              当前选择: {{ selectedPaymentOption || '未选择' }}
            </div>

            <h3 class="form-subtitle mt-20">DANA银行支付</h3>
            <OnPaymentOptionSelector cardType="DANA_VA" :selectedCode="selectedDanaOption"
              @select="handleDanaOptionSelect" />
            <div class="selection-result mt-10">
              当前选择: {{ selectedDanaOption || '未选择' }}
            </div>

            <h3 class="form-subtitle mt-20">马来西亚现金支付</h3>
            <OnPaymentOptionSelector cardType="Cash_MY" :selectedCode="selectedCashMYOption"
              @select="handleCashMYOptionSelect" />
            <div class="selection-result mt-10">
              当前选择: {{ selectedCashMYOption || '未选择' }}
            </div>

            <h3 class="form-subtitle mt-20">马来西亚网上银行支付 (隐藏名称)</h3>
            <OnPaymentOptionSelector cardType="Internet_Banking_MY" :selectedCode="selectedIBMYOption"
              @select="handleIBMYOptionSelect" :showName="false" />
            <div class="selection-result mt-10">
              当前选择: {{ selectedIBMYOption || '未选择' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import {
  OnButton,
  OnCollapsiblePanel,
  OnCollapsiblePanelItem,
  OnInput,
  OnSelect,
  OnAutocomplete,
  OnMessage,
  CardInformationForm,
  CountrySelect,
  OnCountdownTimer,
  OnErrorMsgPanel,
  OnPaymentOptionSelector,
} from '@/componentsV2';
import emailDomains from '@/utils/tools/email';

export default {
  components: {
    OnButton,
    OnInput,
    OnSelect,
    OnAutocomplete,
    OnCollapsiblePanel,
    OnCollapsiblePanelItem,
    CardInformationForm,
    CountrySelect,
    OnCountdownTimer,
    OnErrorMsgPanel,
    OnPaymentOptionSelector,
  },
  data() {
    return {
      activeTab: 'autocomplete', // 修改默认显示为自动完成组件
      name: 'components-demo',
      isLoading: false,
      inputValue: '',
      keepPlaceholderValue: '',
      errorInputValue: '',
      errorMessage: '',
      requiredValue: '',
      requiredValueError: false,
      emailValue: '',
      emailError: '',
      // 下拉框数据
      selectedValue: '',
      selectedValue2: '',
      selectedValue3: '',
      selectedError: '',
      selectErrorMessage: '',
      selectedRequired: '',
      selectRequiredError: false,
      selectOptions: [
        { value: 'option1', label: '选项1' },
        { value: 'option2', label: '选项2' },
        { value: 'option3', label: '选项3' },
        { value: 'option4', label: '选项4' },
      ],
      selectOptionsWithDisabled: [
        { value: 'option1', label: '选项1' },
        { value: 'option2', label: '选项2', disabled: true },
        { value: 'option3', label: '选项3' },
        { value: 'option4', label: '选项4', disabled: true },
      ],
      // 折叠面板数据
      selectedPaymentMethod: 'wechat', // 默认为微信支付
      showPanelArrow: false, // 控制是否显示箭头，默认不显示
      categoryArrowType: 'inherit', // 多选模式箭头显示类型
      paymentMethods: [
        {
          name: 'wechat',
          title: '微信支付',
          icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969929.png',
        },
        {
          name: 'alipay',
          title: '支付宝',
          icon: 'https://cdn-icons-png.flaticon.com/512/6124/6124998.png',
        },
        {
          name: 'credit-card',
          title: '信用卡支付',
          icon: 'https://cdn-icons-png.flaticon.com/512/196/196578.png',
        },
        {
          name: 'disabled',
          title: 'Apple Pay (暂不可用)',
          icon: 'https://cdn-icons-png.flaticon.com/512/2168/2168336.png',
          disabled: true,
        },
      ],
      // 多选模式数据
      selectedCategories: ['category1'],
      categoryItems: [
        { name: 'category1', title: '分类1' },
        { name: 'category2', title: '分类2' },
        { name: 'category3', title: '分类3' },
      ],
      // 卡信息相关数据
      cardInfo: {
        cardNum: '',
        effectiveDate: '',
        cvv: '',
      },
      cardValidationRules: {
        cardNum: [{ name: 'required', required: false }],
        effectiveDate: [{ name: 'required', required: false }],
        cvv: [{ name: 'required', required: false }],
      },
      cardTypeList: [
        { logoPath: require('@/assets/images/cards/visa.png') },
        { logoPath: require('@/assets/images/cards/Master.png') },
        { logoPath: require('@/assets/images/cards/AmericanExpress.png') },
        { logoPath: require('@/assets/images/cards/JCB.png') },
        { logoPath: require('@/assets/images/cards/DinersClub.png') },
        { logoPath: require('@/assets/images/cards/DiscoverCard.png') },
      ],
      showCardResult: false,
      cardValidationSuccess: false,
      selectedCountry: '',
      selectedCountryName: '',
      selectedCommonCountry: '',
      selectedCustomCountry: '',
      selectedErrorCountry: '',
      customCountryList: [
        { value: 'US', label: '美国' },
        { value: 'CA', label: '加拿大' },
        { value: 'GB', label: '英国' },
        { value: 'DE', label: '德国' },
        { value: 'FR', label: '法国' },
        { value: 'JP', label: '日本' },
      ],
      countryError: false,
      countryErrorMessage: '',
      // 倒计时组件数据
      isCountdownSimplified: false,
      justBeforeThreeMin: this.getFixedDemoTime(),
      justStartedTime: this.getFixedDemoTime(1700), // 设置为只过了100秒
      // 错误面板组件数据
      isErrorPanel1Visible: true,
      isErrorPanel2Visible: true,
      // 支付选项选择器数据
      selectedPaymentOption: 'BCA',
      selectedDanaOption: 'bank1',
      selectedCashMYOption: '7eleven',
      selectedIBMYOption: 'maybank',

      // 自动完成组件数据
      autocompleteValue: '',
      autocompleteSuggestions: ['苹果', '香蕉', '橙子', '葡萄', '西瓜', '芒果', '菠萝'],

      // 邮箱输入框
      autocompleteEmailValue: '',
      emailDomains,

      // 对象数组建议
      countryValue: '',
      countrySuggestions: [
        { value: 'CN', label: '中国' },
        { value: 'US', label: '美国' },
        { value: 'JP', label: '日本' },
        { value: 'GB', label: '英国' },
        { value: 'DE', label: '德国' },
        { value: 'FR', label: '法国' },
        { value: 'CA', label: '加拿大' },
      ],

      // 禁用自动完成
      disabledAutocompleteValue: '',

      // 错误状态自动完成
      errorAutocompleteValue: '',
      autocompleteErrorMessage: '',

      // Checkout用法示例
      checkoutEmailValue: '',
    };
  },
  methods: {
    handleClick() {
      alert('按钮被点击了！');
    },
    toggleLoading() {
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
      }, 2000);
    },
    validateInput() {
      if (this.errorInputValue.length < 3) {
        this.errorMessage = '输入内容不能少于3个字符';
      } else {
        this.errorMessage = '';
      }
    },
    validateRequired() {
      this.requiredValueError = !this.requiredValue;
    },
    resetRequiredError() {
      if (this.requiredValue) {
        this.requiredValueError = false;
      }
    },
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.emailValue) {
        this.emailError = '';
      } else if (!emailRegex.test(this.emailValue)) {
        this.emailError = '请输入有效的电子邮箱地址';
      } else {
        this.emailError = '';
      }
    },
    // 下拉框验证方法
    validateSelect() {
      if (this.selectedError === 'option1') {
        this.selectErrorMessage = '不能选择选项1';
      } else {
        this.selectErrorMessage = '';
      }
    },
    validateSelectRequired() {
      this.selectRequiredError = !this.selectedRequired;
    },
    resetSelectRequiredError() {
      if (this.selectedRequired) {
        this.selectRequiredError = false;
      }
    },
    // 获取支付方式名称
    getPaymentMethodName(name) {
      const methodMap = {
        wechat: '微信支付',
        alipay: '支付宝',
        'credit-card': '信用卡支付',
        disabled: 'Apple Pay (暂不可用)',
      };
      return methodMap[name] || name || '未选择';
    },
    handleCategoryChange(activeNames) {
    },
    // 卡信息相关方法
    validateCardInfo() {
      // 设置必填规则
      this.cardValidationRules = {
        cardNum: [{ name: 'required', required: true }],
        effectiveDate: [{ name: 'required', required: true }],
        cvv: [{ name: 'required', required: true }],
      };

      this.showCardResult = true;
      // 获取组件内部的验证结果
      this.$nextTick(() => {
        // 模拟获取验证结果
        const hasCardNum = !!this.cardInfo.cardNum;
        const hasEffectiveDate = !!this.cardInfo.effectiveDate;
        const hasCvv = !!this.cardInfo.cvv;
        this.cardValidationSuccess = hasCardNum && hasEffectiveDate && hasCvv;
      });
    },
    resetCardInfo() {
      this.cardInfo = {
        cardNum: '',
        effectiveDate: '',
        cvv: '',
      };
      // 重置必填规则
      this.cardValidationRules = {
        cardNum: [{ name: 'required', required: false }],
        effectiveDate: [{ name: 'required', required: false }],
        cvv: [{ name: 'required', required: false }],
      };
      this.showCardResult = false;
    },
    handleCardInputFocus(data) {
    },
    logBlurEvent(field, value) {
    },
    handleCountryChange(data) {
      this.selectedCountry = data.code;
      this.selectedCountryName = data.country ? data.country.fullName : '';
    },
    handleCommonCountryChange(data) {
    },
    handleCustomCountryChange(data) {
    },
    validateCountry(data) {
      if (!data.code) {
        this.countryError = true;
        this.countryErrorMessage = '请选择一个有效的国家';
      } else {
        this.countryError = false;
        this.countryErrorMessage = '';
      }
    },
    // 倒计时组件相关方法
    handleCountdownClose() {
      this.$message.info('倒计时组件已切换为简化模式');
    },
    handleCountdownTimeEnd() {
      this.$message.warning('倒计时已结束');
    },
    toggleCountdownMode() {
      this.isCountdownSimplified = !this.isCountdownSimplified;
    },
    resetCountdown() {
      // 重新生成演示用时间
      this.justBeforeThreeMin = this.getFixedDemoTime();
      // 根据当前模式获取正确的ref
      const countdownRef = this.isCountdownSimplified ? 'demoCountdown2' : 'demoCountdown1';
      if (this.$refs[countdownRef]) {
        this.$refs[countdownRef].resetCountdown();
      }
    },
    // 获取固定的演示用时间（已确保可以正常显示）
    getFixedDemoTime(secondsElapsed = 1700) {
      // 固定使用当前时间作为基准，设置merchantTxnTime为当前时间减去指定秒数
      // 30分钟(1800秒)的订单，已经过了secondsElapsed秒，还剩(1800-secondsElapsed)秒
      // 默认设置为已过去1700秒，还剩100秒，确保在3分钟内显示
      return moment().subtract(secondsElapsed, 'seconds').format('YYYY-MM-DD HH:mm:ss');
    },
    // 错误面板组件相关方法
    handleErrorPanel1Close() {
      this.isErrorPanel1Visible = false;
      this.$message.info('错误面板1已关闭');
    },
    handleErrorPanel2Close() {
      this.isErrorPanel2Visible = false;
      this.$message.info('错误面板2已关闭');
    },
    showErrorPanel1() {
      this.isErrorPanel1Visible = true;
    },
    showErrorPanel2() {
      this.isErrorPanel2Visible = true;
    },
    // 支付选项选择器相关方法
    handlePaymentOptionSelect(code) {
      this.selectedPaymentOption = code;
      this.$message.info(`已选择DOKU支付选项: ${code}`);
    },
    handleDanaOptionSelect(code) {
      this.selectedDanaOption = code;
      this.$message.info(`已选择DANA支付选项: ${code}`);
    },
    handleCashMYOptionSelect(code) {
      this.selectedCashMYOption = code;
      this.$message.info(`已选择马来西亚现金支付选项: ${code}`);
    },
    handleIBMYOptionSelect(code) {
      this.selectedIBMYOption = code;
      this.$message.info(`已选择马来西亚网上银行支付选项: ${code}`);
    },

    // 消息提示组件相关方法
    showSuccessMessage() {
      OnMessage.success('Card removed successfully');
    },
    showErrorMessage() {
      OnMessage.error('Error reason');
    },
    showLongSuccessMessage() {
      OnMessage.success('这条成功消息将显示5秒', 500000);
    },
    showLongErrorMessage() {
      OnMessage.error('这条错误消息将显示5秒', 500000);
    },
    showMultipleMessages() {
      OnMessage.success('第一条成功消息');
      setTimeout(() => {
        OnMessage.error('第二条错误消息');
      }, 500);
      setTimeout(() => {
        OnMessage.success('第三条成功消息');
      }, 1000);
    },

    // 自动完成组件相关方法
    // 查询邮箱域名建议
    queryEmailSearch(queryString, cb) {
      let results = [];

      if (queryString.indexOf('@') > -1) {
        const domain = queryString.split('@')[1];
        results = this.emailDomains
          .filter(item => !domain || item.value.startsWith(domain))
          .map(item => {
            const emailPrefix = queryString.split('@')[0];
            return {
              value: emailPrefix + item.value,
            };
          });
      }

      // 调用回调返回结果
      cb(results);
    },

    // 验证自动完成输入
    validateAutocompleteInput() {
      if (!this.errorAutocompleteValue) {
        this.autocompleteErrorMessage = '请输入内容，此字段不能为空';
      } else if (this.errorAutocompleteValue.length < 3) {
        this.autocompleteErrorMessage = '输入内容不能少于3个字符';
      } else {
        this.autocompleteErrorMessage = '';
      }
    },
    // 获取分类箭头显示状态
    getCategoryArrowVisibility() {
      if (this.categoryArrowType === 'show') {
        return true;
      } else if (this.categoryArrowType === 'hide') {
        return false;
      } else {
        // 继承全局设置
        return this.showPanelArrow;
      }
    },
  },
  computed: {
    categoryArrowTypeText() {
      const typeMap = {
        inherit: `继承全局设置 (${this.showPanelArrow ? '显示' : '隐藏'})`,
        show: '强制显示',
        hide: '强制隐藏',
      };
      return typeMap[this.categoryArrowType] || '';
    },

    // 根据输入内容过滤邮箱域名建议
    filteredEmailSuggestions() {
      if (!this.autocompleteEmailValue || !this.autocompleteEmailValue.includes('@')) {
        return [];
      }
      return this.emailDomains;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.components-demo {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  color: #333;

  .page-title {
    text-align: center;
    margin-bottom: 30px;
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }

  .component-tabs {
    display: flex;
    justify-content: center;
    margin-bottom: 30px;

    .tab {
      padding: 12px 24px;
      margin: 0 8px;
      font-size: 16px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s;
      background-color: #f5f5f5;

      &:hover {
        background-color: #eaeaea;
      }

      &.active {
        background-color: $color-purple-primary;
        color: white;
        box-shadow: 0 4px 12px rgba($color-purple-primary, 0.3);
      }
    }
  }

  .component-section {
    margin-bottom: 40px;
  }

  .section-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 20px;
    position: relative;
    padding-left: 15px;
    border-left: 4px solid $color-purple-primary;
  }

  .card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
  }

  .component-card {
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    background: #fff;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    .card-header {
      padding: 14px 20px;
      font-size: 16px;
      font-weight: 500;
      background-color: rgba($color-purple-primary, 0.1);
      color: $color-purple-primary;
      border-bottom: 1px solid rgba($color-purple-primary, 0.1);
    }

    .card-content {
      padding: 20px;
    }
  }

  .result-text {
    margin-top: 10px;
    font-size: 14px;
    color: #666;
  }

  .field-container {
    position: relative;
  }

  .required-error {
    position: absolute;
    top: -18px;
    right: 0;
    font-size: 12px;
    color: $color-form-error;
    z-index: 1;
  }

  .error-message {
    font-size: 12px;
    color: $color-form-error;
    margin-top: 4px;
    text-align: left;
  }

  .feature-hint {
    margin-top: 8px;
    font-size: 13px;
    color: #888;
    font-style: italic;
  }

  // 折叠面板相关样式
  .panel-content {
    p {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    code {
      background-color: #f5f7fa;
      padding: 2px 4px;
      border-radius: 3px;
      font-family: monospace;
      color: $color-purple-primary;
    }
  }

  .custom-title {
    display: flex;
    align-items: center;

    .title-icon {
      margin-right: 8px;
      font-size: 16px;
    }
  }

  // 支付相关样式
  .payment-badge {
    background-color: #10b981;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
  }

  .unavailable-tag {
    background-color: #f3f4f6;
    color: #6b7280;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
  }

  .selection-result {
    padding: 12px;
    background-color: #f0f7ff;
    border-radius: 4px;
    margin-bottom: 16px;
    font-weight: 500;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .mt-10 {
    margin-top: 10px;
  }

  .category-content {
    padding: 10px 0;
  }

  .tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .tag {
    padding: 4px 8px;
    background-color: #f3f4f6;
    border-radius: 4px;
    font-size: 12px;
    color: #4b5563;
  }

  .payment-selector {
    padding-bottom: 0;
  }

  .continue-button {
    margin-top: 16px;
  }

  .qr-code-placeholder {
    width: 200px;
    height: 200px;
    background-color: #f5f5f5;
    border: 1px dashed #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 16px auto;
    color: #999;
  }

  .form-row {
    display: flex;
    gap: 12px;
    margin-top: 12px;
  }

  .form-group {
    margin-bottom: 12px;
    width: 100%;

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 14px;
    }
  }

  .payment-debug {
    margin-top: 16px;
    font-size: 12px;
    color: #666;
  }

  .mt-20 {
    margin-top: 20px;
  }

  .ml-10 {
    margin-left: 10px;
  }

  .mb-10 {
    margin-bottom: 10px;
  }

  .card-info-result {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    margin-top: 20px;

    .result-title {
      font-weight: 600;
      margin-bottom: 10px;
      color: #4b5563;
    }

    .result-item {
      display: flex;
      margin-bottom: 8px;

      .label {
        width: 80px;
        color: #6b7280;
      }

      .value {
        font-weight: 500;
      }

      .text-success {
        color: #10b981;
      }

      .text-error {
        color: #ef4444;
      }
    }
  }

  .full-width {
    grid-column: 1 / -1;
  }

  .card-form-content {
    display: flex;
    flex-direction: column;
  }

  .card-form-wrapper {
    max-width: 450px;
    margin: 0 auto;
    padding: 20px;
    background-color: #f8f9fc;
    border-radius: 16px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  }

  .form-title {
    text-align: center;
    color: #4b5563;
    margin-bottom: 20px;
    font-size: 18px;
  }

  .card-actions {
    display: flex;
    justify-content: center;
  }

  .form-subtitle {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  .panel-controls {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px;
    background-color: #f8f9fc;
    border-radius: 8px;

    .control-label {
      margin-right: 10px;
      font-weight: 500;
    }
  }

  .arrow-toggle-controls {
    margin-bottom: 16px;
  }
}
</style>
